import { IKnmPreview } from 'ApiPackage/SearchModule/interfaces/IKnmPreview';
import { formatDateLocale, formatDate } from 'CorePackage/MainModule/common/formatDate';
import { ROUTE_KNM } from 'CorePackage/MainModule/constants/routes';
import { fetchReasonTitle } from 'CorePackage/MainModule/common/fieldUtils';
import {
  DATE_FORMAT_FULL,
  DATE_FORMAT_LONG_MINUTE,
  DATE_FORMAT_LONG,
} from 'CorePackage/MainModule/constants/dateFormat';

import { KnmSubtitle } from './KnmSubtitle';
import styles from './styles.module.scss';
import { parseErknmValue } from '../common/parseErknmValue';
import { planTypeApprovedSelectOptions, approvedSelectOptions } from '../constants/approvedSelectOptions';
import { SubjectNotifications } from './SubjectNotifications';
import { EpguNotifications } from './EpguNotifications';
import { getSuspensionEvents } from '../common/getSuspensionEvents';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';

interface IErknmCommonProps {
  passportData?: IKnmPreview;
}

export const ErknmCommon = ({ passportData }: IErknmCommonProps) => {
  const currentApprovedSelectOptions =
    passportData?.typeCode === 'PP' ? planTypeApprovedSelectOptions : approvedSelectOptions;
  const renderFzName = (fzName: string) => {
    const splitedName = fzName.split(' ');
    if (splitedName.length > 1) return splitedName.join('-');
    return `${fzName}-ФЗ`;
  };
  const suspensionEvents = getSuspensionEvents(passportData, 'inspectionErknm');

  return (
    <div className={styles.ColumnContent}>
      <KnmSubtitle title="Общий состав атрибутов паспорта КНМ">
        {passportData?.erpId && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Учетный номер КНМ в соответствии с постановлением Правительства РФ от 28 апреля 2015 г. N 415 &quot;О
              Правилах формирования и ведения единого реестра проверок&quot;
            </div>
            <div className={styles.ColValue}>{passportData?.erpId}</div>
          </div>
        )}
        {passportData?.erpCreateDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>
              Дата получения учетного номера КНМ в соответствии с постановлением Правительства РФ от 28 апреля 2015 г. N
              415 «О Правилах формирования и ведения единого реестра проверок»
            </div>
            <div className={styles.ColValue}>
              {formatDateLocale({
                date: passportData?.erpCreateDate,
                dateFormat: DATE_FORMAT_FULL,
                parseFormat: 'yyyy-M-d-H-m-s',
              })}
            </div>
          </div>
        )}
        {passportData?.typeName && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Характер КНМ</div>
            <div className={styles.ColValue}>{passportData?.typeName}</div>
          </div>
        )}
        <div className={styles.Row}>
          <div className={styles.ColText}>Федеральный закон, в соответствии с которым проводится КНМ</div>
          <div className={styles.ColValue}>{passportData?.fzName && renderFzName(passportData?.fzName)}</div>
        </div>
        {passportData?.status && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Статус КНМ</div>
            <div className={styles.ColValue}>{passportData?.status}</div>
          </div>
        )}
        {passportData?.created && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата регистрации в ФГИС ЕРКНМ</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.created, dateFormat: DATE_FORMAT_LONG_MINUTE })}
            </div>
          </div>
        )}
        {passportData?.approve && passportData?.approve.impossibleConductionReason && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Сведения о причинах невозможности проведения КНМ</div>
            <div className={styles.ColValue}>{passportData?.approve.impossibleConductionReason}</div>
          </div>
        )}
        {passportData?.inspectionErknm?.kindKnm && passportData?.inspectionErknm.kindKnm.value && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Вид КНМ</div>
            <div className={styles.ColValue}>
              {parseErknmValue<{ title?: string }>(passportData?.inspectionErknm.kindKnm.value)?.title}
            </div>
          </div>
        )}
      </KnmSubtitle>
      {passportData?.inspectionErknm?.decision &&
        (passportData?.inspectionErknm.decision.dateTimeDecision ||
          passportData?.inspectionErknm.decision.numberDecision ||
          (passportData?.inspectionErknm.decision.notInFIAS
            ? passportData?.inspectionErknm.decision.region && passportData?.inspectionErknm.decision.manualAddress
            : passportData?.inspectionErknm.decision.placeDecision) ||
          passportData?.inspectionErknm.decision.fioSigner ||
          passportData?.inspectionErknm.decision.disabledDecisionMessage ||
          passportData?.inspectionErknm.decision.titleSigner) && (
          <>
            <KnmSubtitle title="Решение о проведении КНМ">
              {passportData?.inspectionErknm.decision.decisionDisabled === true ? (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Сообщение</div>
                  <div className={styles.ColValue}>
                    {passportData?.inspectionErknm.decision.disabledDecisionMessage}
                  </div>
                </div>
              ) : (
                <>
                  {passportData?.inspectionErknm.decision.dateTimeDecision && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Дата и время издания решения</div>
                      <div className={styles.ColValue}>
                        {formatDate({
                          date: passportData?.inspectionErknm.decision.dateTimeDecision,
                          dateFormat: DATE_FORMAT_LONG_MINUTE,
                        })}
                      </div>
                    </div>
                  )}
                  {passportData?.inspectionErknm.decision.numberDecision && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Номер решения</div>
                      <div className={styles.ColValue}>{passportData?.inspectionErknm.decision.numberDecision}</div>
                    </div>
                  )}
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Иное указание места (нет в ФИАС)</div>
                    <div className={styles.ColValue}>
                      <Checkbox
                        checked={!!passportData?.inspectionErknm.decision.notInFIAS}
                        size="medium"
                        type="checkbox"
                        theme="system"
                        disabled={true}
                      />
                    </div>
                  </div>
                  {!passportData?.inspectionErknm.decision.notInFIAS &&
                    passportData?.inspectionErknm.decision.placeDecision && (
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Место вынесения решения</div>
                        <div className={styles.ColValue}>{passportData?.inspectionErknm.decision.placeDecision}</div>
                      </div>
                    )}
                  {!!passportData?.inspectionErknm.decision.notInFIAS &&
                    passportData?.inspectionErknm.decision.manualAddress && (
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Место вынесения решения</div>
                        <div className={styles.ColValue}>{passportData?.inspectionErknm.decision.manualAddress}</div>
                      </div>
                    )}
                  {!!passportData?.inspectionErknm.decision.notInFIAS && passportData?.inspectionErknm.decision.region && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Субъект РФ</div>
                      <div className={styles.ColValue}>{passportData?.inspectionErknm.decision.region}</div>
                    </div>
                  )}
                  {passportData?.inspectionErknm.decision.fioSigner && (
                    <div className={styles.Row}>
                      <div className={styles.ColText}>ФИО должностного лица</div>
                      <div className={styles.ColValue}>{passportData?.inspectionErknm.decision.fioSigner}</div>
                    </div>
                  )}
                  {passportData?.inspectionErknm.decision.titleSigner &&
                    passportData?.inspectionErknm.decision.titleSigner.value && (
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Должность лица, подписавшего решение</div>
                        <div className={styles.ColValue}>
                          {
                            parseErknmValue<{ title?: string }>(
                              passportData?.inspectionErknm.decision.titleSigner.value,
                            )?.title
                          }
                        </div>
                      </div>
                    )}
                </>
              )}
            </KnmSubtitle>
          </>
        )}
      <KnmSubtitle title="Даты проведения КНМ">
        {passportData?.startDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата начала КНМ</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.startDate, dateFormat: DATE_FORMAT_LONG })}
            </div>
          </div>
        )}
        {passportData?.stopDate && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата окончания КНМ</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.stopDate, dateFormat: DATE_FORMAT_LONG })}
            </div>
          </div>
        )}
      </KnmSubtitle>
      {suspensionEvents.length > 0 && (
        <KnmSubtitle title="Сведения о приостановлении срока проведения КНМ">
          {suspensionEvents.map((item, index) => (
            <div key={index}>
              <div className={styles.SectionTitle}>
                {item.decisionNumber
                  ? `Решение о приостановлении № ${item.decisionNumber}`
                  : `Мотивированное представление № ${index + 1}`}
              </div>
              {item.event && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Действие, на основе которого срок проведения КНМ приостановлен</div>
                  <div className={styles.ColValue}>{item.event}</div>
                </div>
              )}
              {item.decisionDateTime && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата и время издания решения</div>
                  <div className={styles.ColValue}>{item.decisionDateTime}</div>
                </div>
              )}
              {item.decisionNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер решения</div>
                  <div className={styles.ColValue}>{item.decisionNumber}</div>
                </div>
              )}
              <div className={styles.Row}>
                <div className={styles.ColText}>Иное указание места (нет в ФИАС)</div>
                <div className={styles.ColValue}>
                  <Checkbox checked={!!item.notInFIAS} size="medium" type="checkbox" theme="system" disabled={true} />
                </div>
              </div>
              {!item.notInFIAS && item.decisionPlace && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Место вынесения решения о приостановлении срока КНМ</div>
                  <div className={styles.ColValue}>{item.decisionPlace}</div>
                </div>
              )}
              {!!item.notInFIAS && item.manualAddress && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Место вынесения решения о приостановлении срока КНМ</div>
                  <div className={styles.ColValue}>{item.manualAddress}</div>
                </div>
              )}
              {!!item.notInFIAS && item.region && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Субъект РФ</div>
                  <div className={styles.ColValue}>{item.region}</div>
                </div>
              )}
              {item.startDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата начала приостановления срока КНМ</div>
                  <div className={styles.ColValue}>{item.startDate}</div>
                </div>
              )}
              {item.stopDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата окончания приостановления срока КНМ</div>
                  <div className={styles.ColValue}>{item.stopDate}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData?.inspectionErknm?.rescheduling &&
        passportData?.inspectionErknm?.rescheduling.map((rescheduling, i) => {
          return (
            <KnmSubtitle
              key={i}
              title={`Перенос проведения мероприятия от ${formatDate({
                date: rescheduling.initiationDate,
                dateFormat: DATE_FORMAT_LONG,
              })}`}
            >
              <div className={styles.Row}>
                <div className={styles.ColText}>Новая дата начала КНМ</div>
                <div className={styles.ColValue}>
                  {formatDate({ date: rescheduling.newStartDate, dateFormat: DATE_FORMAT_LONG })}
                </div>
              </div>
              <div className={styles.Row}>
                <div className={styles.ColText}>Новая дата окончания КНМ</div>
                <div className={styles.ColValue}>
                  {formatDate({ date: rescheduling.newEndDate, dateFormat: DATE_FORMAT_LONG })}
                </div>
              </div>
              {rescheduling.newDirectDurationDays && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Новый срок непосредственного взаимодействия (дней)</div>
                  <div className={styles.ColValue}>{rescheduling.newDirectDurationDays}</div>
                </div>
              )}
              {rescheduling.newDurationHours && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Новый срок непосредственного взаимодействия (дней)</div>
                  <div className={styles.ColValue}>{rescheduling.newDurationHours}</div>
                </div>
              )}
              {rescheduling.comment && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Комментарий</div>
                  <div className={styles.ColValue}>{rescheduling.comment}</div>
                </div>
              )}
            </KnmSubtitle>
          );
        })}
      {passportData?.inspectionErknm?.publicAbrogation && (
        <KnmSubtitle title="Сведения о нормативно-правовых основаниях, повлекших за собой необходимость в запуске процесса отмены проведения мероприятий">
          <div className={styles.Row}>
            <div className={styles.ColText}>Основания отмены мероприятий</div>
            <div className={styles.ColValue}>{passportData?.inspectionErknm?.publicAbrogation.reason}</div>
          </div>
          <div className={styles.Row}>
            <div className={styles.ColText}>Комментарий</div>
            <div className={styles.ColValue}>{passportData?.inspectionErknm?.publicAbrogation.comment || '-'}</div>
          </div>
          {passportData?.inspectionErknm?.publicAbrogation.linkedInspectionId && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Ссылка на профилактический визит</div>
              <div className={styles.ColValue}>
                <a
                  href={`${ROUTE_KNM}/${passportData?.inspectionErknm?.publicAbrogation.linkedInspectionId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {passportData?.inspectionErknm?.publicAbrogation.linkedInspectionErpId}
                </a>
              </div>
            </div>
          )}
        </KnmSubtitle>
      )}
      {passportData?.massCancel?.cancelled && (
        <KnmSubtitle title="Сведения о нормативно-правовых основаниях, повлекших за собой необходимость в запуске процесса отмены проведения мероприятий">
          <div className={styles.Row}>
            <div className={styles.ColText}>Основание массовой отмены</div>
            <div className={styles.ColValue}>{passportData.massCancel.reason}</div>
          </div>
          {passportData.massCancel.reasonFileName && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Файл основания массовой отмены</div>
              <div className={styles.ColValue}>{passportData.massCancel.reasonFileName}</div>
            </div>
          )}
        </KnmSubtitle>
      )}
      {passportData?.inspectionErknm?.reasons && passportData?.inspectionErknm.reasons.length > 0 && (
        <KnmSubtitle title="Перечень основания проведения КНМ">
          {passportData?.inspectionErknm.reasons.map((item, index) => (
            <div key={index}>
              <div className={styles.SectionTitle}>Основание проведения КНМ</div>
              {item.reason && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{fetchReasonTitle(item)}</div>
                </div>
              )}
              {item.reason.programNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер программы проверок</div>
                  <div className={styles.ColValue}>{item.reason.programNumber}</div>
                </div>
              )}
              {item.reason.type.lastConductedKnmDateRequired && item.reason.lastConductedKnmDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.reason.lastConductedKnmDate, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.reason.date && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.reason.date, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.reason.requirementDetails && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Реквизиты требования</div>
                  <div className={styles.ColValue}>{item.reason.requirementDetails}</div>
                </div>
              )}
              {item.reason.dateRequirement && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата требования</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.reason.dateRequirement, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.reason.assignmentNumber && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер поручения</div>
                  <div className={styles.ColValue}>{item.reason.assignmentNumber}</div>
                </div>
              )}
              {item.reason.assignmentDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата поручения</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: item.reason.assignmentDate, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
              )}
              {item.riskIndicators?.map((indicatorValue, indicatorIndex) => {
                return (
                  <>
                    {indicatorValue.riskIndicator && (
                      <div key={indicatorIndex}>
                        <span className={styles.SectionTitle}>Индикатор риска #{indicatorIndex + 1}</span>
                        <div className={styles.Row}>
                          <div className={styles.ColText}>
                            {parseErknmValue<{ title?: string }>(indicatorValue.riskIndicator.value)?.title}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData?.inspectionErknm?.approve?.approveRequired && (
        <KnmSubtitle title="Необходимость согласования">
          <div className={styles.Row}>
            <div className={styles.ColText}>Необходимость согласования</div>
            <div className={styles.ColValue}>{passportData?.inspectionErknm?.approve?.approveRequiredTitle}</div>
          </div>
          {passportData?.inspectionErknm.dataContent && (
            <div className={styles.Row}>
              <div className={styles.ColText}>
                Сведения о причинении вреда (ущерба) или об угрозе причинения вреда (ущерба) охраняемым законом
                ценностям в случаях отсутствия необходимости согласования проведения контрольного (надзорного)
                мероприятия с органами прокуратуры в соответствии со статьей 66 Федерального закона
              </div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm.dataContent}</div>
            </div>
          )}
        </KnmSubtitle>
      )}
      <KnmSubtitle title="Сведения о согласовании проведения КНМ с органами прокуратуры">
        {passportData?.prosecName && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Наименование прокуратуры</div>
            <div className={styles.ColValue}>{passportData?.prosecName}</div>
          </div>
        )}
        {passportData?.inspectionErknm?.approve?.approved && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Решение по заявлению</div>
            <div className={styles.ColValue}>
              {
                currentApprovedSelectOptions.find(item => item.id === passportData?.inspectionErknm?.approve?.approved)
                  ?.label
              }
            </div>
          </div>
        )}
        {passportData?.inspectionErknm?.approve?.rejectReasonType &&
          passportData?.inspectionErknm?.approve?.rejectReasonType.name && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Причина отказа</div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm.approve.rejectReasonType.name}</div>
            </div>
          )}
        {passportData?.inspectionErknm?.approve?.numberDecision && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Номер решения</div>
            <div className={styles.ColValue}>{passportData?.inspectionErknm.approve.numberDecision}</div>
          </div>
        )}
        {passportData?.inspectionErknm?.approve?.dateDecision && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Дата решения</div>
            <div className={styles.ColValue}>
              {formatDate({ date: passportData?.inspectionErknm.approve.dateDecision, dateFormat: DATE_FORMAT_LONG })}
            </div>
          </div>
        )}
        {passportData?.inspectionErknm?.approve?.fioSigner && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Кем принято решение</div>
            <div className={styles.ColValue}>{passportData?.inspectionErknm.approve.fioSigner}</div>
          </div>
        )}
        {passportData?.inspectionErknm?.approve?.titleSigner && (
          <div className={styles.Row}>
            <div className={styles.ColText}>Должность лица, подписавшего решение</div>
            <div className={styles.ColValue}>{passportData?.inspectionErknm.approve.titleSigner}</div>
          </div>
        )}
      </KnmSubtitle>
      <KnmSubtitle title="Предмет контрольного (надзорного) мероприятия">
        {passportData?.inspectionErknm?.requirementsNew && passportData.inspectionErknm.requirementsNew.length > 0 && (
          <KnmSubtitle title="Обязательные требования, подлежащие проверке">
            {passportData?.inspectionErknm.requirementsNew.map((npa, index) => (
              <div key={index}>
                <span className={styles.SectionTitle}>Обязательные требования, подлежащие проверке #{index + 1}</span>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Нормативно-правовой акт</div>
                  <div className={styles.ColValue}>{npa.title}</div>
                </div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Номер</div>
                  <div className={styles.ColValue}>{npa.number}</div>
                </div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата утверждения</div>
                  <div className={styles.ColValue}>
                    {formatDate({ date: npa.acceptanceDate, dateFormat: DATE_FORMAT_LONG })}
                  </div>
                </div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Структурная единица</div>
                  <div className={styles.ColValue}>{npa.structuralUnit.title}</div>
                </div>
              </div>
            ))}
          </KnmSubtitle>
        )}
        {passportData?.inspectionErknm?.docTarget && passportData?.inspectionErknm.docTarget.length > 0 && (
          <KnmSubtitle title="Разрешительные и иные документы">
            {passportData.inspectionErknm.docTarget.map((docTarget, index) => (
              <div key={index}>
                <span className={styles.SectionTitle}>Разрешительный или иной документ #{index + 1}</span>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Категория документа</div>
                  <div className={styles.ColValue}>
                    {docTarget.ervkControlKindDocTarget.type === 'PERMITTED'
                      ? 'Разрешительный документ'
                      : 'Иной документ'}
                  </div>
                </div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Документ</div>
                  <div className={styles.ColValue}>{docTarget.ervkControlKindDocTarget.targetTitle || ''}</div>
                </div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Конкретное проверяемое требование и пункт</div>
                  <div className={styles.ColValue}>{docTarget.specificRequirementAndClause || ''}</div>
                </div>
              </div>
            ))}
          </KnmSubtitle>
        )}
        {passportData?.inspectionErknm?.approveDocs && passportData?.inspectionErknm.approveDocs.length > 0 && (
          <KnmSubtitle title="Разрешительные и иные документы">
            {passportData?.inspectionErknm.approveDocs.map((item, index) => (
              <div className={styles.SectionBlock} key={index}>
                {item.approveDoc && item.approveDoc.value && (
                  <>
                    <div className={styles.SectionTitle}>Документ #{index + 1}</div>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Наименование документа</div>
                      <div className={styles.ColValue}>
                        {parseErknmValue<{ title?: string }>(item.approveDoc.value)?.title}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </KnmSubtitle>
        )}
        {(passportData?.inspectionErknm?.isChecklistsUsed !== null ||
          (passportData?.inspectionErknm?.checklists && passportData?.inspectionErknm.checklists.length > 0)) && (
          <KnmSubtitle title="Сведения о применяемом проверочном листе">
            {passportData?.inspectionErknm?.isChecklistsUsed !== null && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Отметка об использовании проверочного листа</div>
                <div className={styles.ColValue}>{passportData?.inspectionErknm?.isChecklistsUsed ? 'Да' : 'Нет'}</div>
              </div>
            )}
            {passportData?.inspectionErknm?.checklists &&
              passportData?.inspectionErknm.checklists.length > 0 &&
              passportData?.inspectionErknm.checklists.map((item, index) => {
                const checkList =
                  item.checkList &&
                  item.checkList.value &&
                  parseErknmValue<{ title?: string; questions?: { title?: string }[] }>(item.checkList.value);
                return (
                  <div className={styles.SectionBlock} key={index}>
                    {checkList && (
                      <>
                        <div className={styles.SectionTitle}>Проверочный лист #{index + 1}</div>
                        <div className={styles.Row}>
                          <div className={styles.ColText}>Наименование проверочного листа</div>
                          <div className={styles.ColValue}>{checkList.title}</div>
                        </div>
                        {item.ervkCheckList && (
                          <div className={styles.Row}>
                            <div className={styles.ColText}>Номер и название из ЕРВК</div>
                            <div className={styles.ColValue}>
                              {item.ervkCheckList.number} - {item.ervkCheckList.description}
                            </div>
                          </div>
                        )}
                        {checkList.questions && checkList.questions.length > 0 && (
                          <KnmSubtitle title="Контрольные вопросы">
                            {checkList.questions.map((question, questionIndex) => (
                              <>
                                <div className={styles.SectionTitle}>Контрольный вопрос #{questionIndex + 1}</div>
                                <div className={styles.Row}>
                                  <div className={styles.ColText}>Вопрос</div>
                                  <div className={styles.ColValue}>{question.title}</div>
                                </div>
                              </>
                            ))}
                          </KnmSubtitle>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
          </KnmSubtitle>
        )}
        {passportData?.inspectionErknm?.places && passportData?.inspectionErknm.places.length > 0 && (
          <KnmSubtitle title="Место (места) проведения контрольного (надзорного) мероприятия">
            {passportData?.inspectionErknm.places.map((item, index) => (
              <div key={index}>
                <div className={styles.SectionTitle}>Место #{index + 1}</div>
                <div className={styles.Row}>
                  <div className={styles.ColText}>Иное указание места (нет в ФИАС)</div>
                  <div className={styles.ColValue}>
                    <Checkbox checked={!!item.notInFIAS} size="medium" type="checkbox" theme="system" disabled={true} />
                  </div>
                </div>
                {item.value ? (
                  <>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Адрес</div>
                      <div className={styles.ColValue}>{item.value}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Адрес</div>
                      <div className={styles.ColValue}>{item.manualAddress}</div>
                    </div>
                    <div className={styles.Row}>
                      <div className={styles.ColText}>Субъект РФ</div>
                      <div className={styles.ColValue}>{item.region}</div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </KnmSubtitle>
        )}
        {passportData?.inspectionErknm?.organizationDocuments &&
          passportData?.inspectionErknm.organizationDocuments.length > 0 && (
            <KnmSubtitle title="Перечень представленных контролируемыми лицами документов">
              {passportData?.inspectionErknm.organizationDocuments.map((item, index) => (
                <div key={index}>
                  {item.value && (
                    <>
                      <div className={styles.SectionTitle}>Документ #{index + 1}</div>
                      <div className={styles.Row}>
                        <div className={styles.ColText}>Наименование</div>
                        <div className={styles.ColValue}>{item.value}</div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </KnmSubtitle>
          )}
      </KnmSubtitle>
      {passportData?.inspectionErknm?.organizations &&
        passportData.inspectionErknm.organizations.length > 0 &&
        passportData.inspectionErknm.organizations[0].act?.canceledReason && (
          <KnmSubtitle title="Сведения о невозможности проведения КНМ">
            <div className={styles.Row}>
              <div className={styles.ColText}>Причина невозможности проведения</div>
              <div className={styles.ColValue}>{passportData.inspectionErknm.organizations[0].act.canceledReason}</div>
            </div>
          </KnmSubtitle>
        )}
      <SubjectNotifications subjectNotifications={passportData?.subjectNotifications} />
      <EpguNotifications epguNotifications={passportData?.epguNotifications} />
      {(passportData?.inspectionErknm?.isPresence !== null ||
        passportData?.inspectionErknm.isPresenceDelegateFullNames) && (
        <KnmSubtitle title="Отметка о присутствии контролируемого лица и (или) его представителя)">
          {passportData?.inspectionErknm?.isPresence !== null && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка</div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm?.isPresence ? 'Да' : 'Нет'}</div>
            </div>
          )}
          {passportData?.inspectionErknm?.isPresenceDelegateFullNames &&
            passportData?.inspectionErknm.isPresenceDelegateFullNames.length > 0 && (
              <div className={styles.Row}>
                <div className={styles.ColText}>Присутствующие лица</div>
                <div className={styles.ColValue}>
                  {passportData?.inspectionErknm.isPresenceDelegateFullNames.map((item, index) => (
                    <div key={index}>{item.value}</div>
                  ))}
                </div>
              </div>
            )}
        </KnmSubtitle>
      )}
      {(passportData?.inspectionErknm?.isSelection !== null || passportData?.inspectionErknm.selection) && (
        <KnmSubtitle title="Сведения об отборе проб (образцов) продукции (товаров)">
          {passportData?.inspectionErknm?.isSelection !== null && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка</div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm?.isSelection ? 'Да' : 'Нет'}</div>
            </div>
          )}
          {passportData?.inspectionErknm?.selection && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Сведения</div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm.selection}</div>
            </div>
          )}
        </KnmSubtitle>
      )}
      {(passportData?.inspectionErknm?.withVideo !== null ||
        passportData?.inspectionErknm.serviceDocument ||
        passportData?.inspectionErknm.returnSelection !== null) && (
        <KnmSubtitle title="Иная информация">
          {passportData?.inspectionErknm?.withVideo !== null && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка об использовании видеозаписи</div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm?.withVideo ? 'Да' : 'Нет'}</div>
            </div>
          )}
          {passportData?.inspectionErknm?.serviceDocument && (
            <div className={styles.Row}>
              <div className={styles.ColText}>
                Информация о результате экспертизы продукции (товаров), результатов выполненных работ, оказанных услуг
              </div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm.serviceDocument.description}</div>
            </div>
          )}
          {passportData?.inspectionErknm?.returnSelection !== null && (
            <div className={styles.Row}>
              <div className={styles.ColText}>Отметка о возврате товара</div>
              <div className={styles.ColValue}>{passportData?.inspectionErknm?.returnSelection ? 'Да' : 'Нет'}</div>
            </div>
          )}
        </KnmSubtitle>
      )}
    </div>
  );
};
